import React, { useState, useEffect, useRef } from 'react';
import { Button } from 'primereact/button';
import { InputSwitch } from 'primereact/inputswitch';
import { companyService, Company } from '../services/companyService';
import { userService, User } from '../services/userService';
import Dialog from '../components/Dialog';
import RoleGuard from '../components/RoleGuard';
import { Permission } from '../utils/permissions';
import { readImageAsDataUrl } from '../utils/imageFile';
import { getAssetUrl } from '../utils/assets';
import { UPTIMIO_BRAND, contrastTextOn } from '../utils/brand';
import { getProjectsAccess, formatProjectsTrialLabel } from '../utils/projectsAccess';
import { useAuth } from '../context/AuthContext';
import { UserRole } from '../types';
import './CompanyManagement.css';

const CORRESPONDENT_SLOTS = [1, 2, 3, 4, 5] as const;

type CorrespondentBank = { bank: string; swift: string; country: string };

const emptyCorrespondent = (): CorrespondentBank => ({ bank: '', swift: '', country: '' });

const emptyBilling = () => ({
  issuer: '',
  contactName: '',
  hourlyRate: 0,
  currency: 'USD',
  taxId: '',
  swiftCode: '',
  iban: '',
  cardNumber: '',
  bankName: '',
  correspondentBank1: '',
  correspondentBank2: '',
  correspondentBank3: '',
  correspondentBank4: '',
  correspondentBank5: '',
  correspondentCountry1: '',
  correspondentCountry2: '',
  correspondentCountry3: '',
  correspondentCountry4: '',
  correspondentCountry5: '',
  correspondentSwift1: '',
  correspondentSwift2: '',
  correspondentSwift3: '',
  correspondentSwift4: '',
  correspondentSwift5: '',
  paymentInstructions: ''
});

const correspondentsFromBilling = (billing?: Record<string, any> | null): CorrespondentBank[] => {
  return CORRESPONDENT_SLOTS.map((n) => ({
    bank: billing?.[`correspondentBank${n}`] || '',
    swift: billing?.[`correspondentSwift${n}`] || '',
    country: billing?.[`correspondentCountry${n}`] || ''
  })).filter((item) => item.bank || item.swift || item.country);
};

const billingWithCorrespondents = (
  billing: ReturnType<typeof emptyBilling>,
  correspondents: CorrespondentBank[]
) => {
  const next: Record<string, string | number> = { ...billing };
  CORRESPONDENT_SLOTS.forEach((n) => {
    const item = correspondents[n - 1];
    next[`correspondentBank${n}`] = item?.bank || '';
    next[`correspondentSwift${n}`] = item?.swift || '';
    next[`correspondentCountry${n}`] = item?.country || '';
  });
  return { ...billing, ...next };
};

const projectsModuleHint = (company: Company | null) => {
  if (!company || company.isOwner) {
    return 'Always on for the system company';
  }
  const access = getProjectsAccess(company);
  if (access.kind === 'trial') {
    const trial = formatProjectsTrialLabel(access.endsAt);
    return `Demo active — ${trial.label} (until ${trial.dateLabel}). Toggle on to unlock permanently.`;
  }
  if (access.kind === 'expired') {
    return 'The 7-day demo has ended. Toggle on to unlock permanently.';
  }
  if (access.kind === 'hidden') {
    return 'The client hid Projects from their menu. Toggle on to unlock it for them.';
  }
  return 'Unlock the Projects Kanban, or the client admin can start a 7-day demo from Projects.';
};

const CompanyManagement: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | undefined>();
  const [iconPreview, setIconPreview] = useState<string | undefined>();
  const [pendingLogo, setPendingLogo] = useState<string | undefined>();
  const [pendingIcon, setPendingIcon] = useState<string | undefined>();
  const [logoUploading, setLogoUploading] = useState(false);
  const [iconUploading, setIconUploading] = useState(false);
  const [logoError, setLogoError] = useState('');
  const [iconError, setIconError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<{ company: Company; ticketCount: number } | null>(null);
  const [deleteTickets, setDeleteTickets] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [ticketBusy, setTicketBusy] = useState(false);
  const [projectBusy, setProjectBusy] = useState(false);
  const [projectsPurgeTarget, setProjectsPurgeTarget] = useState<{
    company: Company;
    projectCount: number;
    taskCount: number;
    fileCount: number;
  } | null>(null);
  const [disableProjectsModule, setDisableProjectsModule] = useState(true);
  const importInputRef = useRef<HTMLInputElement>(null);
  const importProjectsInputRef = useRef<HTMLInputElement>(null);
  const importProjectsCompanyIdRef = useRef<string | null>(null);
  const [importCompanyId, setImportCompanyId] = useState<string | null>(null);
  const [dialog, setDialog] = useState<{
    isOpen: boolean;
    type: 'alert' | 'confirm';
    variant: 'info' | 'success' | 'warning' | 'danger';
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    type: 'alert',
    variant: 'info',
    title: '',
    message: '',
    onConfirm: () => {}
  });
  const [formData, setFormData] = useState({
    name: '',
    type: 'CLIENT' as 'SERVICE_PROVIDER' | 'CLIENT',
    email: '',
    phone: '',
    address: {
      street: '',
      city: '',
      state: '',
      country: '',
      zipCode: ''
    },
    status: 'ACTIVE' as 'ACTIVE' | 'INACTIVE' | 'SUSPENDED',
    primaryColor: '',
    sidebarColor: '',
    sidebarTextColor: '',
    buttonTextColor: '',
    projectsEnabled: false,
    billing: emptyBilling()
  });
  const [correspondents, setCorrespondents] = useState<CorrespondentBank[]>([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [companiesData, usersData] = await Promise.all([
        companyService.getCompanies(),
        userService.getUsers()
      ]);
      setCompanies(companiesData);
      setUsers(usersData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const refreshIfCurrentCompany = async (companyId: string) => {
    if (user?.company?._id === companyId) {
      await refreshUser();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        status: formData.status,
        branding: {
          primaryColor: formData.primaryColor || undefined,
          sidebarColor: formData.sidebarColor || undefined,
          sidebarTextColor: formData.sidebarTextColor || undefined,
          buttonTextColor: formData.buttonTextColor || undefined
        },
        billing: billingWithCorrespondents(formData.billing, correspondents),
        modules: user?.role === UserRole.SUPER_ADMIN
          ? { projects: formData.projectsEnabled }
          : undefined
      };

      if (editingCompany) {
        await companyService.updateCompany(editingCompany._id, payload);
        await refreshIfCurrentCompany(editingCompany._id);
      } else {
        const created = await companyService.createCompany(payload);
        if (pendingLogo) {
          await companyService.uploadLogo(created._id, pendingLogo);
        }
        if (pendingIcon) {
          await companyService.uploadIcon(created._id, pendingIcon);
        }
      }
      await loadData();
      setShowModal(false);
      setEditingCompany(null);
      resetForm();
    } catch (error) {
      console.error('Error saving company:', error);
    }
  };

  const handleEdit = (company: Company) => {
    setEditingCompany(company);
    setLogoPreview(getAssetUrl(company.logo));
    setIconPreview(getAssetUrl(company.icon));
    setPendingLogo(undefined);
    setPendingIcon(undefined);
    setLogoError('');
    setIconError('');
    setFormData({
      name: company.name,
      type: company.type,
      email: company.email,
      phone: company.phone || '',
      address: {
        street: company.address?.street || '',
        city: company.address?.city || '',
        state: company.address?.state || '',
        country: company.address?.country || '',
        zipCode: company.address?.zipCode || ''
      },
      status: company.status,
      primaryColor: company.branding?.primaryColor || '',
      sidebarColor: company.branding?.sidebarColor || '',
      sidebarTextColor: company.branding?.sidebarTextColor || '',
      buttonTextColor: company.branding?.buttonTextColor || '',
      projectsEnabled: Boolean(company.isOwner || company.modules?.projects),
      billing: {
        ...emptyBilling(),
        issuer: company.billing?.issuer || (company.isOwner ? (company.billing?.contactName || '') : ''),
        contactName: company.billing?.contactName || '',
        hourlyRate: company.billing?.hourlyRate || 0,
        currency: company.billing?.currency || 'USD',
        taxId: company.billing?.taxId || '',
        swiftCode: company.billing?.swiftCode || '',
        iban: company.billing?.iban || '',
        cardNumber: company.billing?.cardNumber || '',
        bankName: company.billing?.bankName || '',
        correspondentBank1: company.billing?.correspondentBank1 || '',
        correspondentBank2: company.billing?.correspondentBank2 || '',
        correspondentBank3: company.billing?.correspondentBank3 || '',
        correspondentBank4: company.billing?.correspondentBank4 || '',
        correspondentBank5: company.billing?.correspondentBank5 || '',
        correspondentCountry1: company.billing?.correspondentCountry1 || '',
        correspondentCountry2: company.billing?.correspondentCountry2 || '',
        correspondentCountry3: company.billing?.correspondentCountry3 || '',
        correspondentCountry4: company.billing?.correspondentCountry4 || '',
        correspondentCountry5: company.billing?.correspondentCountry5 || '',
        correspondentSwift1: company.billing?.correspondentSwift1 || '',
        correspondentSwift2: company.billing?.correspondentSwift2 || '',
        correspondentSwift3: company.billing?.correspondentSwift3 || '',
        correspondentSwift4: company.billing?.correspondentSwift4 || '',
        correspondentSwift5: company.billing?.correspondentSwift5 || '',
        paymentInstructions: company.billing?.paymentInstructions || ''
      }
    });
    setCorrespondents(company.isOwner ? correspondentsFromBilling(company.billing) : []);
    setShowModal(true);
  };

  const handleDelete = async (company: Company) => {
    try {
      const stats = await companyService.getCompanyStats(company._id);
      setDeleteTickets(false);
      setDeleteTarget({ company, ticketCount: stats.ticketCount || 0 });
    } catch (error) {
      setDeleteTickets(false);
      setDeleteTarget({ company, ticketCount: 0 });
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) {
      return;
    }

    if (deleteTarget.ticketCount > 0 && !deleteTickets) {
      setDialog({
        isOpen: true,
        type: 'alert',
        variant: 'warning',
        title: 'Tickets still exist',
        message: `This company has ${deleteTarget.ticketCount} tickets. Export them first, or check “Also delete all tickets” to remove them with the company.`,
        onConfirm: () => setDialog({ ...dialog, isOpen: false })
      });
      return;
    }

    try {
      setDeleting(true);
      await companyService.deleteCompany(deleteTarget.company._id, deleteTickets);
      setDeleteTarget(null);
      await loadData();
      setDialog({
        isOpen: true,
        type: 'alert',
        variant: 'success',
        title: 'Success',
        message: deleteTickets && deleteTarget.ticketCount > 0
          ? `Company deleted, including ${deleteTarget.ticketCount} tickets.`
          : 'Company successfully deleted.',
        onConfirm: () => setDialog({ ...dialog, isOpen: false })
      });
    } catch (error: any) {
      const apiMessage = error.response?.data?.message;
      setDialog({
        isOpen: true,
        type: 'alert',
        variant: 'danger',
        title: 'Error',
        message: apiMessage || 'Error deleting company. It may have associated users.',
        onConfirm: () => setDialog({ ...dialog, isOpen: false })
      });
    } finally {
      setDeleting(false);
    }
  };

  const downloadJson = (data: unknown, filename: string) => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExportTickets = async (company: Company) => {
    try {
      setTicketBusy(true);
      const payload = await companyService.exportTickets(company._id);
      const slug = company.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'company';
      const date = new Date().toISOString().slice(0, 10);
      downloadJson(payload, `tickets-${slug}-${date}.json`);
    } catch (error: any) {
      setDialog({
        isOpen: true,
        type: 'alert',
        variant: 'danger',
        title: 'Export failed',
        message: error.response?.data?.message || 'Error exporting tickets.',
        onConfirm: () => setDialog({ ...dialog, isOpen: false })
      });
    } finally {
      setTicketBusy(false);
    }
  };

  const companySlug = (company: Company) =>
    company.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'company';

  const handleExportProjects = async (company: Company) => {
    try {
      setProjectBusy(true);
      const payload = await companyService.exportProjects(company._id);
      const date = new Date().toISOString().slice(0, 10);
      downloadJson(payload, `projects-${companySlug(company)}-${date}.json`);
    } catch (error: any) {
      setDialog({
        isOpen: true,
        type: 'alert',
        variant: 'danger',
        title: 'Export failed',
        message: error.response?.data?.message || 'Error exporting projects.',
        onConfirm: () => setDialog({ ...dialog, isOpen: false })
      });
    } finally {
      setProjectBusy(false);
    }
  };

  const handleDeleteProjectsClick = async (company: Company) => {
    try {
      setProjectBusy(true);
      const stats = await companyService.getProjectDataStats(company._id);
      setDisableProjectsModule(true);
      setProjectsPurgeTarget({ company, ...stats });
    } catch (error: any) {
      setDialog({
        isOpen: true,
        type: 'alert',
        variant: 'danger',
        title: 'Could not load projects',
        message: error.response?.data?.message || 'Error loading project data.',
        onConfirm: () => setDialog({ ...dialog, isOpen: false })
      });
    } finally {
      setProjectBusy(false);
    }
  };

  const confirmDeleteProjects = async () => {
    if (!projectsPurgeTarget) return;
    try {
      setProjectBusy(true);
      const result = await companyService.deleteProjects(projectsPurgeTarget.company._id, disableProjectsModule);
      setProjectsPurgeTarget(null);
      if (editingCompany?._id === projectsPurgeTarget.company._id && disableProjectsModule) {
        setFormData((prev) => ({ ...prev, projectsEnabled: false }));
        setEditingCompany((prev) =>
          prev
            ? {
                ...prev,
                modules: {
                  ...(prev.modules || {}),
                  projects: false,
                  projectsHidden: true
                }
              }
            : prev
        );
      }
      await loadData();
      setDialog({
        isOpen: true,
        type: 'alert',
        variant: 'success',
        title: 'Projects deleted',
        message: `Removed ${result.projectCount} projects, ${result.taskCount} tasks, and ${result.fileCount} files from disk.`,
        onConfirm: () => setDialog({ ...dialog, isOpen: false })
      });
    } catch (error: any) {
      setDialog({
        isOpen: true,
        type: 'alert',
        variant: 'danger',
        title: 'Delete failed',
        message: error.response?.data?.message || 'Error deleting projects.',
        onConfirm: () => setDialog({ ...dialog, isOpen: false })
      });
    } finally {
      setProjectBusy(false);
    }
  };

  const handleImportClick = (companyId: string) => {
    setImportCompanyId(companyId);
    importInputRef.current?.click();
  };

  const handleImportFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    const companyId = importCompanyId;
    event.target.value = '';

    if (!file || !companyId) {
      return;
    }

    try {
      setTicketBusy(true);
      const text = await file.text();
      const payload = JSON.parse(text);
      const result = await companyService.importTickets(companyId, payload);
      setDialog({
        isOpen: true,
        type: 'alert',
        variant: 'success',
        title: 'Import complete',
        message: `Imported ${result.imported} tickets${result.skipped ? `, skipped ${result.skipped}` : ''}${result.messagesImported ? `, including ${result.messagesImported} chat messages` : ''}.`,
        onConfirm: () => setDialog({ ...dialog, isOpen: false })
      });
    } catch (error: any) {
      setDialog({
        isOpen: true,
        type: 'alert',
        variant: 'danger',
        title: 'Import failed',
        message: error.response?.data?.message || error.message || 'Invalid JSON file or import error.',
        onConfirm: () => setDialog({ ...dialog, isOpen: false })
      });
    } finally {
      setTicketBusy(false);
      setImportCompanyId(null);
    }
  };

  const handleImportProjectsClick = (companyId: string) => {
    importProjectsCompanyIdRef.current = companyId;
    importProjectsInputRef.current?.click();
  };

  const handleImportProjectsFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    const companyId = importProjectsCompanyIdRef.current;
    event.target.value = '';

    if (!file || !companyId) {
      return;
    }

    try {
      setProjectBusy(true);
      const text = await file.text();
      const parsed = JSON.parse(text);
      const payload =
        parsed?.kind === 'uptimio-projects' || Array.isArray(parsed?.projects)
          ? parsed
          : parsed?.data;
      const result = await companyService.importProjects(companyId, payload);
      if (result.imported > 0 && editingCompany?._id === companyId) {
        setFormData((prev) => ({ ...prev, projectsEnabled: true }));
        setEditingCompany((prev) =>
          prev
            ? {
                ...prev,
                modules: {
                  ...(prev.modules || {}),
                  projects: true,
                  projectsHidden: false
                }
              }
            : prev
        );
      }
      await loadData();
      setDialog({
        isOpen: true,
        type: 'alert',
        variant: 'success',
        title: 'Import complete',
        message: `Imported ${result.imported} projects, ${result.tasksImported} tasks${result.filesImported ? `, ${result.filesImported} files` : ''}${result.skipped ? `, skipped ${result.skipped}` : ''}.`,
        onConfirm: () => setDialog({ ...dialog, isOpen: false })
      });
    } catch (error: any) {
      setDialog({
        isOpen: true,
        type: 'alert',
        variant: 'danger',
        title: 'Import failed',
        message: error.response?.data?.message || error.message || 'Invalid JSON file or import error.',
        onConfirm: () => setDialog({ ...dialog, isOpen: false })
      });
    } finally {
      setProjectBusy(false);
      importProjectsCompanyIdRef.current = null;
    }
  };

  const handleLogoSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setLogoError('');

    try {
      setLogoUploading(true);
      const dataUrl = await readImageAsDataUrl(file);
      if (editingCompany) {
        const logoPath = await companyService.uploadLogo(editingCompany._id, dataUrl);
        setLogoPreview(getAssetUrl(logoPath));
        setEditingCompany({ ...editingCompany, logo: logoPath });
        await loadData();
        await refreshIfCurrentCompany(editingCompany._id);
      } else {
        setPendingLogo(dataUrl);
        setLogoPreview(dataUrl);
      }
    } catch (error: any) {
      const message = error.response?.data?.message || error.message || 'Error uploading logo';
      setLogoError(message);
    } finally {
      setLogoUploading(false);
      event.target.value = '';
    }
  };

  const handleRemoveLogo = async () => {
    setLogoError('');

    try {
      setLogoUploading(true);
      if (editingCompany?.logo) {
        await companyService.removeLogo(editingCompany._id);
        setEditingCompany({ ...editingCompany, logo: undefined });
        await loadData();
        await refreshIfCurrentCompany(editingCompany._id);
      }
      setPendingLogo(undefined);
      setLogoPreview(undefined);
    } catch (error: any) {
      const message = error.response?.data?.message || 'Error removing logo';
      setLogoError(message);
    } finally {
      setLogoUploading(false);
    }
  };

  const handleIconSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setIconError('');

    try {
      setIconUploading(true);
      const dataUrl = await readImageAsDataUrl(file);
      if (editingCompany) {
        const iconPath = await companyService.uploadIcon(editingCompany._id, dataUrl);
        setIconPreview(getAssetUrl(iconPath));
        setEditingCompany({ ...editingCompany, icon: iconPath });
        await loadData();
        await refreshIfCurrentCompany(editingCompany._id);
      } else {
        setPendingIcon(dataUrl);
        setIconPreview(dataUrl);
      }
    } catch (error: any) {
      const message = error.response?.data?.message || error.message || 'Error uploading icon';
      setIconError(message);
    } finally {
      setIconUploading(false);
      event.target.value = '';
    }
  };

  const handleRemoveIcon = async () => {
    setIconError('');

    try {
      setIconUploading(true);
      if (editingCompany?.icon) {
        await companyService.removeIcon(editingCompany._id);
        setEditingCompany({ ...editingCompany, icon: undefined });
        await loadData();
        await refreshIfCurrentCompany(editingCompany._id);
      }
      setPendingIcon(undefined);
      setIconPreview(undefined);
    } catch (error: any) {
      const message = error.response?.data?.message || 'Error removing icon';
      setIconError(message);
    } finally {
      setIconUploading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      type: 'CLIENT',
      email: '',
      phone: '',
      address: {
        street: '',
        city: '',
        state: '',
        country: '',
        zipCode: ''
      },
      status: 'ACTIVE',
      primaryColor: '',
      sidebarColor: '',
      sidebarTextColor: '',
      buttonTextColor: '',
      projectsEnabled: false,
      billing: emptyBilling()
    });
    setCorrespondents([]);
    setLogoPreview(undefined);
    setIconPreview(undefined);
    setPendingLogo(undefined);
    setPendingIcon(undefined);
    setLogoError('');
    setIconError('');
  };

  const getCompanyUsers = (companyId: string) => {
    return users.filter(user => user.company._id === companyId);
  };

  const previewSidebar = formData.sidebarColor || UPTIMIO_BRAND.sidebarColor;
  const previewPrimary = formData.primaryColor || UPTIMIO_BRAND.primaryColor;
  const previewMenuText = formData.sidebarTextColor || contrastTextOn(previewSidebar);
  const previewButtonText = formData.buttonTextColor || contrastTextOn(previewPrimary);

  if (loading) {
    return <>
      <div className="spinner-wrapper">
        <div className="spinner">
          <div className="sk-folding-cube">
            <div className="sk-cube1 sk-cube"></div>
            <div className="sk-cube2 sk-cube"></div>
            <div className="sk-cube4 sk-cube"></div>
            <div className="sk-cube3 sk-cube"></div>
          </div>
        </div>
      </div>
    </>;
  }

  return (
    <div className="company-management">
      <div className="page-header">
        <div>
          <h1 className="page-title">Company Management</h1>
          <p className="page-subtitle">Manage companies, branding, logos and company details</p>
        </div>
        <RoleGuard permissions={[Permission.CREATE_COMPANIES]}>
          <Button
            label="Add Company"
            icon="pi pi-plus"
            className="new-ticket-btn"
            onClick={() => {
              setEditingCompany(null);
              resetForm();
              setShowModal(true);
            }}
          />
        </RoleGuard>
      </div>

      <div className="companies-grid">
        {companies.map(company => {
          const companyUsers = getCompanyUsers(company._id);
          return (
            <div key={company._id} className="company-card">
              <div className="company-header">
                <div className="company-title-row">
                  {(company.icon || company.logo) ? (
                    <img
                      src={getAssetUrl(company.icon || company.logo)}
                      alt={`${company.name} logo`}
                      className="company-card-logo"
                    />
                  ) : (
                    <div className="company-card-logo company-card-logo-empty">
                      <i className="pi pi-building"></i>
                    </div>
                  )}
                  <div className="company-title-text">
                    <h3>
                      {company.name}
                      {company.isOwner && <span className="system-badge">System</span>}
                      {company.branding?.primaryColor && (
                        <span
                          className="company-color-dot"
                          style={{ background: company.branding.primaryColor }}
                          title={company.branding.primaryColor}
                        />
                      )}
                    </h3>
                    <span className="company-email">{company.email}</span>
                  </div>
                </div>
                <div className={`status-badge status-${company.status.toLowerCase()}`}>
                  {company.status}
                </div>
              </div>

              <div className="company-meta">
                {company.phone && <span><i className="pi pi-phone"></i> {company.phone}</span>}
                {company.address && [company.address.city, company.address.country].filter(Boolean).length > 0 && (
                  <span>
                    <i className="pi pi-map-marker"></i>{' '}
                    {[company.address.city, company.address.country].filter(Boolean).join(', ')}
                  </span>
                )}
              </div>

              <div className="company-users">
                {companyUsers.length > 0 ? (
                  <div className="company-user-chips">
                    {companyUsers.slice(0, 4).map(user => (
                      <span key={user._id} className="company-user-chip" title={`${user.email} · ${user.role}`}>
                        {user.firstName} {user.lastName}
                      </span>
                    ))}
                    {companyUsers.length > 4 && (
                      <span className="company-user-chip more">+{companyUsers.length - 4}</span>
                    )}
                  </div>
                ) : (
                  <span className="company-users-empty">No users yet</span>
                )}
              </div>

              <div className="company-actions">
                <RoleGuard permissions={[Permission.UPDATE_COMPANIES, Permission.MANAGE_OWN_COMPANY]}>
                  <Button
                    icon="pi pi-pencil"
                    className="p-button-secondary p-button-sm p-button-rounded compact-action-btn"
                    onClick={() => handleEdit(company)}
                    title="Edit"
                  />
                </RoleGuard>
                <RoleGuard permissions={[Permission.VIEW_COMPANIES]}>
                  <Button
                    icon="pi pi-download"
                    className="p-button-secondary p-button-sm p-button-rounded compact-action-btn"
                    onClick={() => handleExportTickets(company)}
                    disabled={ticketBusy}
                    title="Export tickets"
                  />
                  <Button
                    icon="pi pi-upload"
                    className="p-button-secondary p-button-sm p-button-rounded compact-action-btn"
                    onClick={() => handleImportClick(company._id)}
                    disabled={ticketBusy}
                    title="Import tickets"
                  />
                </RoleGuard>
                {user?.role === UserRole.SUPER_ADMIN && !company.isOwner && (
                  <>
                    <Button
                      icon="pi pi-th-large"
                      className="p-button-secondary p-button-sm p-button-rounded compact-action-btn"
                      onClick={() => handleExportProjects(company)}
                      disabled={projectBusy}
                      title="Export projects and tasks"
                    />
                    <Button
                      icon="pi pi-file-import"
                      className="p-button-secondary p-button-sm p-button-rounded compact-action-btn"
                      onClick={() => handleImportProjectsClick(company._id)}
                      disabled={projectBusy}
                      title="Import projects JSON"
                    />
                  </>
                )}
                <RoleGuard permissions={[Permission.DELETE_COMPANIES]}>
                  {!company.isOwner && (
                    <Button
                      icon="pi pi-trash"
                      className="p-button-danger p-button-sm p-button-rounded compact-action-btn"
                      onClick={() => handleDelete(company)}
                      title="Delete"
                    />
                  )}
                </RoleGuard>
              </div>
            </div>
          );
        })}
      </div>

      <input
        ref={importInputRef}
        type="file"
        accept="application/json,.json"
        hidden
        onChange={handleImportFile}
      />
      <input
        ref={importProjectsInputRef}
        type="file"
        accept="application/json,.json"
        hidden
        onChange={handleImportProjectsFile}
      />

      {deleteTarget && (
        <div className="modal-overlay">
          <div className="modal delete-company-modal">
            <div className="modal-header">
              <div className="modal-header-icon">
                <i className="pi pi-trash"></i>
              </div>
              <div className="app-modal-header-text">
                <h2>Delete company</h2>
                <p>This action cannot be undone.</p>
              </div>
              <button
                type="button"
                className="close-btn"
                onClick={() => setDeleteTarget(null)}
                aria-label="Close"
              >
                <i className="pi pi-times"></i>
              </button>
            </div>
            <div className="modal-form">
              <p>
                Are you sure you want to delete <strong>{deleteTarget.company.name}</strong>?
                Users of this company will also be removed.
              </p>
              {deleteTarget.ticketCount > 0 ? (
                <>
                  <label className="delete-tickets-check">
                    <input
                      type="checkbox"
                      checked={deleteTickets}
                      onChange={(e) => setDeleteTickets(e.target.checked)}
                    />
                    <span>Also delete all {deleteTarget.ticketCount} tickets of this company</span>
                  </label>
                  <small>Export the tickets first if you want to keep them.</small>
                </>
              ) : (
                <small>This company has no tickets.</small>
              )}
              <div className="modal-actions">
                <Button
                  type="button"
                  label="Cancel"
                  icon="pi pi-times"
                  className="cancel-btn p-button-secondary"
                  onClick={() => setDeleteTarget(null)}
                  disabled={deleting}
                />
                <Button
                  type="button"
                  label="Delete company"
                  icon="pi pi-trash"
                  className="p-button-danger"
                  onClick={confirmDelete}
                  loading={deleting}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {projectsPurgeTarget && (
        <div className="modal-overlay">
          <div className="modal delete-company-modal">
            <div className="modal-header">
              <div className="modal-header-icon">
                <i className="pi pi-th-large"></i>
              </div>
              <div className="app-modal-header-text">
                <h2>Delete project data</h2>
                <p>This removes boards, tasks, comments, and uploaded files from disk.</p>
              </div>
              <button
                type="button"
                className="close-btn"
                onClick={() => setProjectsPurgeTarget(null)}
                aria-label="Close"
              >
                <i className="pi pi-times"></i>
              </button>
            </div>
            <div className="modal-form">
              <p>
                Permanently delete Projects data for <strong>{projectsPurgeTarget.company.name}</strong>?
              </p>
              <p>
                {projectsPurgeTarget.projectCount} projects, {projectsPurgeTarget.taskCount} tasks, {projectsPurgeTarget.fileCount} files.
              </p>
              <small>Export the JSON first if you want to keep a copy. This cannot be undone.</small>
              <label className="delete-tickets-check">
                <input
                  type="checkbox"
                  checked={disableProjectsModule}
                  onChange={(e) => setDisableProjectsModule(e.target.checked)}
                />
                <span>Also turn off the Projects module for this company</span>
              </label>
              <div className="modal-actions">
                <Button
                  type="button"
                  label="Cancel"
                  icon="pi pi-times"
                  className="cancel-btn p-button-secondary"
                  onClick={() => setProjectsPurgeTarget(null)}
                  disabled={projectBusy}
                />
                <Button
                  type="button"
                  label="Delete project data"
                  icon="pi pi-trash"
                  className="p-button-danger"
                  onClick={confirmDeleteProjects}
                  loading={projectBusy}
                  disabled={projectsPurgeTarget.projectCount === 0 && projectsPurgeTarget.taskCount === 0 && projectsPurgeTarget.fileCount === 0}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      <Dialog
        isOpen={dialog.isOpen}
        type={dialog.type}
        variant={dialog.variant}
        title={dialog.title}
        message={dialog.message}
        confirmText={dialog.type === 'confirm' ? 'Delete' : 'OK'}
        cancelText="Cancel"
        onConfirm={dialog.onConfirm}
        onCancel={() => setDialog({ ...dialog, isOpen: false })}
      />

      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <div className="modal-header-icon">
                <i className={editingCompany ? 'pi pi-building' : 'pi pi-plus'}></i>
              </div>
              <div className="app-modal-header-text">
                <h2>{editingCompany ? 'Edit Company' : 'Add Company'}</h2>
                <p>
                  {editingCompany?.isOwner
                    ? 'System company — invoices and deals use the Issuer field, not the company name'
                    : editingCompany
                      ? 'Update company details, colors and branding'
                      : 'Fill out the form to create a new company'}
                </p>
              </div>
              <button
                type="button"
                className="close-btn"
                onClick={() => setShowModal(false)}
                aria-label="Close"
              >
                <i className="pi pi-times"></i>
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-group">
                <label><i className="pi pi-briefcase"></i> Company Name <span className="required">*</span></label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Company name"
                  required
                />
                {editingCompany?.isOwner && (
                  <small className="field-hint">Internal name. Not printed on invoices or deals.</small>
                )}
              </div>

              {editingCompany?.isOwner && (
                <div className="form-group">
                  <label><i className="pi pi-id-card"></i> Issuer</label>
                  <input
                    type="text"
                    value={formData.billing.issuer}
                    onChange={(e) => setFormData({ ...formData, billing: { ...formData.billing, issuer: e.target.value } })}
                    placeholder="Your name, legal entity, or DBA"
                  />
                  <small className="field-hint">Shown on invoices and deals instead of the company name.</small>
                </div>
              )}

              {user?.role === UserRole.SUPER_ADMIN && (
                <div className="form-group">
                  <label><i className="pi pi-th-large"></i> Modules</label>
                  <label className="notify-toggle" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
                    <span>
                      <strong>Projects</strong>
                      <small className="field-hint" style={{ display: 'block' }}>
                        {projectsModuleHint(editingCompany)}
                      </small>
                    </span>
                    <InputSwitch
                      checked={editingCompany?.isOwner || formData.projectsEnabled}
                      disabled={Boolean(editingCompany?.isOwner)}
                      onChange={(e) => setFormData({ ...formData, projectsEnabled: !!e.value })}
                    />
                  </label>
                  {editingCompany && !editingCompany.isOwner && (
                    <div className="projects-data-actions">
                      <button
                        type="button"
                        className="text-btn"
                        disabled={projectBusy}
                        onClick={() => handleExportProjects(editingCompany)}
                      >
                        Export projects &amp; tasks
                      </button>
                      <button
                        type="button"
                        className="text-btn"
                        disabled={projectBusy}
                        onClick={() => handleImportProjectsClick(editingCompany._id)}
                      >
                        Import projects JSON
                      </button>
                      <button
                        type="button"
                        className="text-btn is-danger"
                        disabled={projectBusy}
                        onClick={() => handleDeleteProjectsClick(editingCompany)}
                      >
                        Delete all project data
                      </button>
                    </div>
                  )}
                </div>
              )}

              <div className="branding-section">
                <div className="branding-section-header">
                  <label><i className="pi pi-palette"></i> Branding</label>
                  <button
                    type="button"
                    className="branding-reset"
                    onClick={() => setFormData({ ...formData, primaryColor: '', sidebarColor: '', sidebarTextColor: '', buttonTextColor: '' })}
                  >
                    Reset colors
                  </button>
                </div>
                <div className="branding-assets">
                  <div className="branding-asset">
                    {logoPreview ? (
                      <img src={logoPreview} alt="Logo preview" className="company-logo-preview" />
                    ) : (
                      <div className="company-logo-preview">Logo</div>
                    )}
                    <div className="branding-asset-actions">
                      <label className="file-btn">
                        Upload logo
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp,image/gif"
                          onChange={handleLogoSelect}
                          disabled={logoUploading}
                          hidden
                        />
                      </label>
                      {logoPreview && (
                        <button type="button" className="text-btn" onClick={handleRemoveLogo} disabled={logoUploading}>
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="branding-asset">
                    {iconPreview ? (
                      <img src={iconPreview} alt="Icon preview" className="company-icon-preview" />
                    ) : (
                      <div className="company-icon-preview">Icon</div>
                    )}
                    <div className="branding-asset-actions">
                      <label className="file-btn">
                        Upload icon
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp,image/gif"
                          onChange={handleIconSelect}
                          disabled={iconUploading}
                          hidden
                        />
                      </label>
                      {iconPreview && (
                        <button type="button" className="text-btn" onClick={handleRemoveIcon} disabled={iconUploading}>
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                {(logoError || iconError) && (
                  <div className="form-error">{logoError || iconError}</div>
                )}
                <div className="branding-colors">
                  <label className="brand-color-field">
                    <span className="brand-color-title">Menu background</span>
                    <span className="brand-color-hint">The left navigation bar</span>
                    <input
                      type="color"
                      value={previewSidebar}
                      onChange={(e) => setFormData({ ...formData, sidebarColor: e.target.value })}
                    />
                  </label>
                  <label className="brand-color-field">
                    <span className="brand-color-title">Menu text</span>
                    <span className="brand-color-hint">Item names in the menu</span>
                    <input
                      type="color"
                      value={previewMenuText}
                      onChange={(e) => setFormData({ ...formData, sidebarTextColor: e.target.value })}
                    />
                  </label>
                  <label className="brand-color-field">
                    <span className="brand-color-title">Buttons</span>
                    <span className="brand-color-hint">Save, New ticket, and highlights</span>
                    <input
                      type="color"
                      value={previewPrimary}
                      onChange={(e) => setFormData({ ...formData, primaryColor: e.target.value })}
                    />
                  </label>
                  <label className="brand-color-field">
                    <span className="brand-color-title">Button text</span>
                    <span className="brand-color-hint">Label on primary buttons</span>
                    <input
                      type="color"
                      value={previewButtonText}
                      onChange={(e) => setFormData({ ...formData, buttonTextColor: e.target.value })}
                    />
                  </label>
                </div>
                <div className="branding-live-preview">
                  <div
                    className="brand-preview-nav"
                    style={{ background: previewSidebar, color: previewMenuText }}
                  >
                    <span className="brand-preview-nav-title">Menu</span>
                    <span className="brand-preview-item">Tickets</span>
                    <span
                      className="brand-preview-item is-active"
                      style={{ background: previewPrimary, color: previewButtonText }}
                    >
                      Profile
                    </span>
                  </div>
                  <div
                    className="brand-preview-btn"
                    style={{ background: previewPrimary, color: previewButtonText }}
                  >
                    Save
                  </div>
                </div>
                <small>Max 200KB per image. Users see this after sign-in.</small>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label><i className="pi pi-envelope"></i> Email <span className="required">*</span></label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="company@email.com"
                    required
                  />
                </div>

                <div className="form-group">
                  <label><i className="pi pi-phone"></i> Phone</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="Phone number"
                  />
                </div>

                <div className="form-group">
                  <label><i className="pi pi-info-circle"></i> Status <span className="required">*</span></label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value as any})}
                    required
                  >
                    <option value="ACTIVE">Active</option>
                    <option value="INACTIVE">Inactive</option>
                    <option value="SUSPENDED">Suspended</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label><i className="pi pi-map-marker"></i> Street</label>
                <input
                  type="text"
                  value={formData.address.street}
                  onChange={(e) => setFormData({...formData, address: {...formData.address, street: e.target.value}})}
                  placeholder="Street address"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>City</label>
                  <input
                    type="text"
                    value={formData.address.city}
                    onChange={(e) => setFormData({...formData, address: {...formData.address, city: e.target.value}})}
                  />
                </div>
                <div className="form-group">
                  <label>State/Region</label>
                  <input
                    type="text"
                    value={formData.address.state}
                    onChange={(e) => setFormData({...formData, address: {...formData.address, state: e.target.value}})}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Zip Code</label>
                  <input
                    type="text"
                    value={formData.address.zipCode}
                    onChange={(e) => setFormData({...formData, address: {...formData.address, zipCode: e.target.value}})}
                  />
                </div>
                <div className="form-group">
                  <label>Country</label>
                  <input
                    type="text"
                    value={formData.address.country}
                    onChange={(e) => setFormData({...formData, address: {...formData.address, country: e.target.value}})}
                  />
                </div>
              </div>

              {user?.role === UserRole.SUPER_ADMIN && (
                editingCompany?.isOwner ? (
                  <div className="branding-section">
                    <div className="branding-section-header">
                      <label><i className="pi pi-credit-card"></i> How clients pay you</label>
                    </div>
                    <p className="section-hint">These details are printed on every invoice you send. They are not the client’s data.</p>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Bank name</label>
                        <input
                          type="text"
                          value={formData.billing.bankName}
                          onChange={(e) => setFormData({ ...formData, billing: { ...formData.billing, bankName: e.target.value } })}
                        />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>SWIFT</label>
                        <input
                          type="text"
                          value={formData.billing.swiftCode}
                          onChange={(e) => setFormData({ ...formData, billing: { ...formData.billing, swiftCode: e.target.value } })}
                        />
                      </div>
                      <div className="form-group">
                        <label>IBAN</label>
                        <input
                          type="text"
                          value={formData.billing.iban}
                          onChange={(e) => setFormData({ ...formData, billing: { ...formData.billing, iban: e.target.value } })}
                        />
                      </div>
                    </div>
                    <div className="correspondent-block">
                      <div className="correspondent-block-header">
                        <label>Correspondent banks (optional)</label>
                        {correspondents.length < CORRESPONDENT_SLOTS.length && (
                          <button
                            type="button"
                            className="text-btn"
                            onClick={() => setCorrespondents((current) => [...current, emptyCorrespondent()])}
                          >
                            + Add bank
                          </button>
                        )}
                      </div>
                      {correspondents.length === 0 ? (
                        <p className="section-hint">Add only if a payment needs an intermediary bank.</p>
                      ) : (
                        <div className="correspondent-list">
                          {correspondents.map((item, index) => (
                            <div className="correspondent-card" key={index}>
                              <div className="correspondent-card-header">
                                <span>Correspondent {index + 1}</span>
                                <button
                                  type="button"
                                  className="text-btn"
                                  onClick={() => setCorrespondents((current) => current.filter((_, i) => i !== index))}
                                >
                                  Remove
                                </button>
                              </div>
                              <div className="form-group">
                                <label>Bank name</label>
                                <input
                                  type="text"
                                  value={item.bank}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    setCorrespondents((current) => current.map((row, i) => (
                                      i === index ? { ...row, bank: value } : row
                                    )));
                                  }}
                                  placeholder="e.g. Raiffeisen Bank International AG"
                                />
                              </div>
                              <div className="form-row">
                                <div className="form-group">
                                  <label>SWIFT/BIC</label>
                                  <input
                                    type="text"
                                    value={item.swift}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      setCorrespondents((current) => current.map((row, i) => (
                                        i === index ? { ...row, swift: value } : row
                                      )));
                                    }}
                                    placeholder="e.g. RZBAATWW"
                                  />
                                </div>
                                <div className="form-group">
                                  <label>Country</label>
                                  <input
                                    type="text"
                                    value={item.country}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      setCorrespondents((current) => current.map((row, i) => (
                                        i === index ? { ...row, country: value } : row
                                      )));
                                    }}
                                    placeholder="e.g. Austria"
                                  />
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="form-group">
                      <label>Card number (optional)</label>
                      <input
                        type="text"
                        value={formData.billing.cardNumber}
                        onChange={(e) => setFormData({ ...formData, billing: { ...formData.billing, cardNumber: e.target.value } })}
                        placeholder="Only if you accept card payments"
                      />
                    </div>
                    <div className="form-group">
                      <label>Payment instructions (optional)</label>
                      <input
                        type="text"
                        value={formData.billing.paymentInstructions}
                        onChange={(e) => setFormData({ ...formData, billing: { ...formData.billing, paymentInstructions: e.target.value } })}
                        placeholder="Extra note printed on the invoice"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="branding-section">
                    <div className="branding-section-header">
                      <label><i className="pi pi-file"></i> Invoicing this client</label>
                    </div>
                    <p className="section-hint">Used on invoices you send to this company, and for generating invoices from hours.</p>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Name on invoice</label>
                        <input
                          type="text"
                          value={formData.billing.contactName}
                          onChange={(e) => setFormData({ ...formData, billing: { ...formData.billing, contactName: e.target.value } })}
                          placeholder="Billing contact / company legal name"
                        />
                      </div>
                      <div className="form-group">
                        <label>Tax ID (optional)</label>
                        <input
                          type="text"
                          value={formData.billing.taxId}
                          onChange={(e) => setFormData({ ...formData, billing: { ...formData.billing, taxId: e.target.value } })}
                        />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Hourly rate</label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={formData.billing.hourlyRate}
                          onChange={(e) => setFormData({ ...formData, billing: { ...formData.billing, hourlyRate: Number(e.target.value) || 0 } })}
                          placeholder="What you charge this client"
                        />
                      </div>
                      <div className="form-group">
                        <label>Currency</label>
                        <input
                          type="text"
                          value={formData.billing.currency}
                          onChange={(e) => setFormData({ ...formData, billing: { ...formData.billing, currency: e.target.value } })}
                          placeholder="USD"
                        />
                      </div>
                    </div>
                  </div>
                )
              )}

              <div className="modal-actions">
                <Button
                  type="button"
                  label="Cancel"
                  icon="pi pi-times"
                  className="cancel-btn p-button-secondary"
                  onClick={() => setShowModal(false)}
                />
                <Button
                  type="submit"
                  label={editingCompany ? 'Save Changes' : 'Create Company'}
                  icon="pi pi-check"
                  className="submit-btn"
                />
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyManagement;
